// One place to hand over audio: drop a folder, a zip or files, or pick them.
// Presentation and the browser's file plumbing only — what is kept is decided
// in lib/web-upload/intake.ts.

import { useRef, useState, type DragEvent } from "react";
import { FolderOpen, Files, Loader2 } from "lucide-react";
import { cn } from "@web-shared/lib/utils";
import { AUDIO_ACCEPT } from "@/lib/web-upload/config";

export function UploadDropZone({
  busy,
  onDrop,
  onPick,
}: {
  busy: string | null;
  onDrop: (dt: DataTransfer) => void;
  onPick: (files: FileList) => void;
}) {
  const [over, setOver] = useState(false);
  const folder = useRef<HTMLInputElement>(null);
  const files = useRef<HTMLInputElement>(null);

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (!busy) onDrop(e.dataTransfer);
  };
  const picked = (input: HTMLInputElement | null) => {
    if (input?.files && input.files.length > 0) onPick(input.files);
    if (input) input.value = "";
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      className={cn(
        "flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors",
        over ? "border-primary bg-primary/5" : "border-hairline bg-raised",
      )}
    >
      {busy ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {busy}
        </p>
      ) : (
        <>
          {/* A touch screen has nothing to drop and no folder picker: its
              files are a bounce, a voice memo, stems in Files, or a zip. */}
          <div>
            <p className="text-base font-semibold pointer-coarse:hidden">Drop your stems folder or a zip here</p>
            <p className="hidden text-base font-semibold pointer-coarse:block">Your stems, a bounce or a voice memo</p>
            <p className="mt-1 text-xs text-muted-foreground">WAV, AIFF, FLAC, MP3 or M4A · or a zip</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => folder.current?.click()}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-white/15 px-4 text-sm font-semibold hover:bg-white/5 pointer-coarse:hidden"
            >
              <FolderOpen className="size-4" /> Choose a folder
            </button>
            <button
              type="button"
              onClick={() => files.current?.click()}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-white/15 px-4 text-sm font-semibold hover:bg-white/5 pointer-coarse:h-12 pointer-coarse:border-primary pointer-coarse:bg-primary pointer-coarse:px-6 pointer-coarse:text-primary-foreground"
            >
              <Files className="size-4" /> Choose files
            </button>
          </div>
        </>
      )}
      <input
        ref={folder}
        type="file"
        className="hidden"
        // A folder picker: the browser walks it and fills webkitRelativePath.
        {...{ webkitdirectory: "", directory: "" }}
        onChange={(e) => picked(e.currentTarget)}
      />
      <input
        ref={files}
        type="file"
        multiple
        accept={AUDIO_ACCEPT}
        className="hidden"
        onChange={(e) => picked(e.currentTarget)}
      />
    </div>
  );
}
